/**
 * @file MeetingNotesTierView.tsx
 * @brief The Notes tab of a Meeting Notes recording on the web: private line,
 *        "Upgrade to full intelligence", the summary, and the owner's own
 *        notes enhanced with what was said (play to hear it).
 *
 * @details
 * ADR-030 (DES 06-10-2026), the web twin of the phone's MeetingNotesTierView.
 * A Notes meeting has no decisions, key points, mind map, agenda check or PDF
 * by design, so the full Intel layout (and its "will appear after
 * intelligence is compiled" placeholders) does not apply. What it shows:
 *   1. For the owner, the Upgrade button with a confirm. That only the
 *      owner can see it is said by the tab heading's subtitle
 *      (MeetingDetailView NOTES_SUMMARY_HEADING); a lock line here repeated
 *      it (DES 07-10-2026, the phone's 2cc8908).
 *   2. While processing: "Writing your notes…" (calm recorder, DES
 *      19-10-2026: the recorder ends straight here), with the owner's own
 *      typed lines under it so nothing they wrote waits on the model.
 *   3. The summary the one Notes model call wrote (overview + paragraphs).
 *   4. "Your notes": every note the viewer linked to this meeting (the
 *      recorder's notepad), each line with what the meeting said about it,
 *      via NoteMeetingReports. The owner's own words lead; the model's
 *      additions sit below them in secondary text.
 * To-dos stay in the Tasks tab and the transcript in Transcript.
 *
 * Meeting Notes v2 (DES 09-10-2026, the phone's 07-10-2026): when the meeting
 * has a notes doc (`notes_doc`, or the dev preview's), MeetingNotesDocView
 * replaces 3 and 4: sectioned notes with the typed points inside them. A
 * meeting without a doc keeps the v1 summary and "Your notes".
 *
 * Upgrade re-runs the full pipeline on the same recording. On success the
 * meeting is `processing` again with tier 'full' (useUpgradeMeetingToFull
 * writes both detail caches), so the page falls back to the normal full
 * layout and, after diarization, the normal speaker-naming panel, exactly as
 * after recording a full meeting. Server refusals (not enough Intelligence
 * minutes, still processing, still being billed) are shown as they are.
 *
 * @dependencies
 * - ../api: useUpgradeMeetingToFull
 * - features/notes/NoteMeetingReports: the enhanced notes
 * - components/ui/AlertDialog, Button
 *
 * @references
 * - FRONTEND/src/screens/MeetingIntelligence/MeetingDetails/components/MeetingNotesTierView.tsx
 * - BACKEND/src/features/boardroom/routes.js (POST /sessions/:id/upgrade)
 * - docs/decisions/ADR-030-meeting-notes-tier.md
 *
 * @author DES
 * @date 06-10-2026
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LoaderCircle, Sparkles } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Button } from "@/components/ui/Button";
import { ApiClientError } from "@/shared/api/client";
import { useAuth } from "@/shared/auth/auth-context";
import { toast } from "@/shared/hooks/useToast";
import { billingAudienceFor } from "@/features/my-billing/billingErrors";
import { showBillingErrorToast } from "@/features/my-billing/billingToast";
import { isRTLText } from "@/shared/utils/formatters";
import { cn } from "@/shared/theme/cn";
import { MeetingBlockHeading } from "./MeetingBlockHeading";
import { meetingBodyLeading } from "./meetingNameHighlight";
import { NoteMeetingReports } from "@/features/notes/NoteMeetingReports";
import { readPointLevels } from "@/features/notes/pointLevels";
import { MeetingNotesDocView } from "./MeetingNotesDocView";
import type { NotesDoc } from "../notesDoc";
import { useUpgradeMeetingToFull } from "../api";
import type { MeetingAttachedNote, MeetingDetail } from "../schemas";

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------

/** The phone layout's subheading (MeetingDetailView PhoneSubheading): the
 *  drawer heading while MEETING_GRID_HEADINGS is on (DES 08-10-2026). */
function Subheading({ children }: { children: string }) {
  return <MeetingBlockHeading>{children}</MeetingBlockHeading>;
}

/** A note's lines: its points, else a typed note's body (MeetingDetailView
 *  attachedNotePoints, the same rule). */
function noteLines(note: MeetingAttachedNote): string[] {
  const src = note.points?.trim() ? note.points : note.source === "typed" ? note.transcript_text : "";
  return String(src ?? "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
}

function paragraphs(text: string): string[] {
  return text
    .split(/\n{2,}|\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

// ----------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------
type Props = {
  meeting: MeetingDetail;
  /** The viewer owns the meeting (and is not the super admin's read-only view). */
  isOwner: boolean;
  /** The signed-in user, to show only their own notes' reports. */
  viewerId: string | null;
  /** Notes linked to this meeting (useMeetingAttachedNotes). */
  attachedNotes: MeetingAttachedNote[];
  /** The meeting's output language is right to left. */
  contentRtl: boolean;
  /** Meeting Notes v2: the stored doc, or the dev preview's; null = v1. */
  notesDoc?: NotesDoc | null;
};

export function MeetingNotesTierView({
  meeting,
  isOwner,
  viewerId,
  attachedNotes,
  contentRtl,
  notesDoc = null,
}: Props) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const upgrade = useUpgradeMeetingToFull();
  const { user } = useAuth();
  const navigate = useNavigate();

  const status = meeting.status;
  const busy = status === "uploading" || status === "processing" || status === "synthesis_processing";
  // The server accepts an upgrade from ready or error only (MEETING_BUSY else).
  // Only a real Notes meeting: the dev preview sits on a full one (DES
  // 09-10-2026, the phone's `meeting.tier === 'notes'` check).
  const canUpgrade =
    isOwner &&
    meeting.tier === "notes" &&
    !meeting.upgraded_at &&
    (status === "ready" || status === "error");
  const minutes = Math.max(1, Math.ceil(Number(meeting.total_duration_seconds ?? 0) / 60));
  const ownNotes = attachedNotes.filter((n) => !viewerId || !n.owner_id || n.owner_id === viewerId);
  const overview = meeting.meeting_overview?.trim() ?? "";
  const summary = (meeting.meeting_summary ?? meeting.summary_overview ?? "").trim();

  const handleUpgrade = async () => {
    try {
      await upgrade.mutateAsync(meeting.id);
      setConfirmOpen(false);
      toast({
        variant: "success",
        title: "Upgrade started",
        description: "The recording is being processed again. Name the speakers when it is ready.",
      });
    } catch (err) {
      setConfirmOpen(false);
      // UPGRADE_NEEDS_MINUTES / BILLING_PENDING / no card → the Billing screen
      // that fixes it (17-10-2026).
      if (
        err instanceof ApiClientError &&
        showBillingErrorToast({
          code: err.code,
          audience: billingAudienceFor(user),
          serverMessage: err.message,
          navigate: (to) => navigate(to),
        })
      ) {
        return;
      }
      toast({
        variant: "destructive",
        title: "Upgrade not started",
        description: err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  return (
    <div>
      {/* 1. Upgrade (privacy is said by the heading's subtitle). */}
      {canUpgrade ? (
        <Button
          type="button"
          variant="secondary"
          onClick={() => setConfirmOpen(true)}
          className="mb-6"
        >
          <Sparkles className="h-[18px] w-[18px]" aria-hidden />
          Upgrade to full intelligence
        </Button>
      ) : isOwner || meeting.tier !== "notes" ? null : (
        // Sharing v2 (DES 21-10-2026): someone it is shared with reads the
        // Notes view; only the owner can turn it into full intelligence.
        <p className="mb-6 flex items-start gap-2 rounded-2xl bg-black/[0.04] px-4 py-3 text-sm text-app-text-secondary dark:bg-white/[0.06]">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden />
          Want speakers, decisions and to-dos? The owner can turn this into full Meeting
          Intelligence.
        </p>
      )}

      {/* 2. Processing / failed */}
      {busy ? (
        <section className="mb-6" role="status" aria-live="polite">
          <div className="flex items-center gap-3 rounded-2xl bg-black/[0.04] px-4 py-4 dark:bg-white/[0.06]">
            <LoaderCircle className="h-5 w-5 shrink-0 animate-spin text-brand" aria-hidden />
            <span className="min-w-0">
              <span className="block text-[17px] font-bold text-app-text">
                {isOwner ? "Writing your notes…" : "Writing the notes…"}
              </span>
              <span className="block text-sm text-app-text-secondary">
                {ownNotes.some((n) => noteLines(n).length > 0)
                  ? "Your own notes are below. The AI notes join them here when ready; this page updates by itself."
                  : "They appear here when ready; this page updates by itself."}
              </span>
            </span>
          </div>
          {ownNotes.map((n) => {
            const lines = noteLines(n);
            if (lines.length === 0) return null;
            const levels = readPointLevels(n.point_levels, lines.length);
            return (
              <div key={n.id} className="mt-5">
                <Subheading>Your notes</Subheading>
                <ul className="list-disc space-y-1 pl-5 text-base leading-6 text-app-text">
                  {lines.map((l, i) => (
                    <li
                      key={i}
                      dir={isRTLText(l) ? "rtl" : "ltr"}
                      className={levels?.[i] ? "ml-5 list-[circle]" : undefined}
                    >
                      {l}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </section>
      ) : status === "error" ? (
        <p className="mb-6 rounded-xl bg-destructive/10 p-3 text-sm text-destructive">
          These notes could not be finished. Use Retry processing in the meeting menu.
        </p>
      ) : null}

      {/* v2 (DES 09-10-2026): sectioned notes replace the summary and the
          per-note reports; the typed points are inside them. */}
      {notesDoc ? <MeetingNotesDocView doc={notesDoc} meeting={meeting} /> : null}

      {/* 3. Summary */}
      {!notesDoc && (overview || summary) ? (
        <section className="mb-6">
          <Subheading>Summary</Subheading>
          {overview ? (
            <p
              dir={contentRtl || isRTLText(overview) ? "rtl" : "ltr"}
              className={cn(
                "mb-3 text-base font-medium text-app-text",
                meetingBodyLeading(contentRtl || isRTLText(overview)),
              )}
            >
              {overview}
            </p>
          ) : null}
          <div className="space-y-3">
            {paragraphs(summary).map((para, i) => (
              <p
                key={i}
                dir={contentRtl || isRTLText(para) ? "rtl" : "ltr"}
                className={cn(
                  "text-base text-app-text-secondary",
                  meetingBodyLeading(contentRtl || isRTLText(para)),
                )}
              >
                {para}
              </p>
            ))}
          </div>
        </section>
      ) : null}

      {/* 4. Your notes, enhanced */}
      {!busy && !notesDoc ? (
        <section className="mb-6">
          <Subheading>Your notes</Subheading>
          {ownNotes.length === 0 ? (
            <p className="text-[15px] leading-[22px] text-app-text-secondary">
              Notes you type on the recorder appear here, each line with what was said around it.
            </p>
          ) : (
            ownNotes.map((n) => {
              const lines = noteLines(n);
              // Sub-points (DES 12-10-2026): indented under their point.
              const levels = readPointLevels(n.point_levels, lines.length);
              return (
                <NoteMeetingReports
                  key={n.id}
                  noteId={n.id}
                  meetingId={meeting.id}
                  noteTitle={n.title ?? ""}
                  fallbackRecordingUrl={meeting.recording_url}
                  noteVersion={lines.join("\n")}
                  fallback={
                    // No report to show (reports off, or the note has no
                    // lines yet): the note as written, so it is never lost.
                    lines.length ? (
                      <ul className="mb-6 list-disc space-y-1 pl-5 text-base leading-6 text-app-text">
                        {lines.map((l, i) => (
                          <li
                            key={i}
                            dir={isRTLText(l) ? "rtl" : "ltr"}
                            className={levels?.[i] ? "ml-5 list-[circle]" : undefined}
                          >
                            {l}
                          </li>
                        ))}
                      </ul>
                    ) : null
                  }
                />
              );
            })
          )}
        </section>
      ) : null}

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!upgrade.isPending) setConfirmOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Upgrade to full intelligence?</AlertDialogTitle>
            <AlertDialogDescription>
              {`The same recording is processed again with named speakers, decisions checked against the transcript, agenda check, translation and PDF minutes. It uses ${minutes} Intelligence minute${minutes === 1 ? "" : "s"}, and the meeting becomes visible to its participants.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {/* Cancel is a grey text link (sheet standard). */}
            <AlertDialogCancel
              disabled={upgrade.isPending}
              className="border-0 bg-transparent text-muted-foreground hover:bg-transparent hover:text-foreground"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={upgrade.isPending}
              onClick={(e) => {
                // Stay open while the request runs; closed by handleUpgrade.
                e.preventDefault();
                void handleUpgrade();
              }}
            >
              {upgrade.isPending ? (
                <>
                  <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                  Upgrading…
                </>
              ) : (
                "Upgrade"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
